import {
	Button,
	Flex,
	Icon,
	bodyFontFamily,
	colors,
	scale,
	Text,
} from "@capioweb/ui-components/src"
import styled from "styled-components"
import { AutoSuggest } from "./AutoSuggest"
import React from "react"
import { AppContext, LocalesContext } from "../../../context"
import { useNavigate } from "react-router-dom"
import { CommonSearch } from "../SearchHeroBlock"
import { sendCommonSearchesDataLayerPush, urlIsInternal } from "../../../utils"
import { StringListProperty } from "../../../@types/EpiFoundationSpa/Property"

interface SearchComponentProps {
	query?: string
	commonSearches?: CommonSearch[]
	commonSearchesTitle?: StringListProperty
	isStartPage?: boolean
}

const FormStyled = styled.form`
	margin: auto;
	max-width: ${scale.px(69)};
	width: 100%;
	display: flex;
	justify-content: space-between;
	border: 1px solid ${colors.blue33};
	border-radius: 8px;
	margin-bottom: 0;
	background-color: white;
	z-index: 15;

	&:focus-within {
		outline: 2px solid ${colors.navy};
		border-radius: 8px;
	}
`

const InputWrapperStyled = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
`

const InputStyled = styled.input`
	width: 100%;
	border: none;
	font-size: ${scale.px(2)};
	line-height: ${scale.px(3)};
	font-weight: 400;
	padding-top: ${scale.px(2)};
	padding-bottom: ${scale.px(2)};
	color: ${colors.primary};
	background-color: white;

	&:focus-visible {
		outline: none;
	}

	&::placeholder {
		font-size: 16px;
		font-family: ${bodyFontFamily};
	}
`

const SearchComponent: React.FC<SearchComponentProps> = ({
	query,
	isStartPage,
	commonSearches,
	commonSearchesTitle,
}) => {
	const [input, setInput] = React.useState<string>(query ?? "")
	const [autoSuggestQuery, setAutoSuggestQuery] = React.useState<string>("")
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const { getUserLocation } = React.useContext(AppContext)
	const navigate = useNavigate()
	const searchUrl = (query: string) => `/sok?query=${query}`

	const MapCommonSearches = (view: "mobile" | "desktop") => {
		const isMobile = view === "mobile"
		const range = isMobile ? 3 : commonSearches.length
		return (
			<Flex
				flexWrap="wrap"
				justifyContent={isMobile ? "center" : "flex-start"}
				rowGap={1}
				columnGap={{ _: 2, m: 1 }}
				maxw={1}
			>
				{commonSearches.slice(0, range).map((commonSearch: CommonSearch) => {
					return (
						<Flex key={`common-search-${commonSearch.text}`} maxw={1}>
							<Button.Link
								to={commonSearch.link || searchUrl(commonSearch.text)}
								target={
									commonSearch.link && !urlIsInternal(commonSearch.link)
										? "_blank"
										: "_self"
								}
								variant="purple"
								onClick={() =>
									sendCommonSearchesDataLayerPush(commonSearch.text)
								}
								style={{ display: "flex", maxWidth: "100%" }}
							>
								<Text variant="paragraph-14" mb={0} truncate="singleLine">
									{commonSearch.text}
								</Text>
							</Button.Link>
						</Flex>
					)
				})}
			</Flex>
		)
	}
	return (
		<>
			<FormStyled
				onSubmit={(e) => {
					e.preventDefault()
					navigate(searchUrl(input), { replace: true })
				}}
			>
				<Flex w={1}>
					<InputWrapperStyled>
						<Flex flexDirection="column">
							<Icon icon="search" my="auto" ml={2} mr={1}></Icon>
						</Flex>
						<Flex width={1} pr={2} flexDirection="column">
							<Flex my="auto">
								<InputStyled
									type="search"
									placeholder={translate(translationKeys.searchPlaceholder)}
									onChange={(event) => {
										setAutoSuggestQuery(event.target.value)
										setInput(event.target.value)
									}}
									defaultValue={query}
								></InputStyled>
							</Flex>
						</Flex>
					</InputWrapperStyled>
					{/* Search submit button desktop (inside input field) */}
					<Flex display={{ _: "none", m: "flex" }} my={1.5} mx={1}>
						<Button.Link to={searchUrl(input)}>
							{translate(translationKeys.search)}
						</Button.Link>
					</Flex>
				</Flex>
			</FormStyled>
			{autoSuggestQuery.length >= 3 && (
				<AutoSuggest query={autoSuggestQuery} isSearchPopup={false} />
			)}
			{/* Search submit button mobile (outside input field) */}
			<Flex
				display={{ _: "flex", m: "none" }}
				w={1}
				mt={2}
				mb={isStartPage ? 2 : 1.5}
				maxw={scale.px(69)}
				mx="auto"
			>
				<Button.Link to={searchUrl(input)} style={{ width: "100%" }}>
					{translate(translationKeys.search)}
				</Button.Link>
			</Flex>
			{isStartPage && commonSearches?.length > 0 && (
				<Flex
					maxw={scale.px(69)}
					mx="auto"
					w={{ _: 1, m: "auto" }}
					mb={2}
					flexDirection="column"
					mt={{ _: 0, m: 2 }}
				>
					<Text textAlign={{ _: "center", l: "left" }} mb={1} mt={0}>
						{commonSearchesTitle.value}
					</Text>
					{/* Mobile view */}
					<Flex display={{ _: "flex", m: "none" }} maxw={1}>
						{MapCommonSearches("mobile")}
					</Flex>
					{/* Desktop view */}
					<Flex display={{ _: "none", m: "flex" }} maxw={{ l: 57 }}>
						{MapCommonSearches("desktop")}
					</Flex>
				</Flex>
			)}
		</>
	)
}

export { SearchComponent }
